import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import Schedule from "./pages/Schedule";
import Absences from "./pages/Absences";
import Users from "./pages/Users";
import Stats from "./pages/Stats";
import ReportingPlanning from "./pages/ReportingPlanning";
import LeaveManagement from "./pages/LeaveManagement";
import Admin from "./pages/Admin";
import Profile from "./pages/Profile";
import TeamCommunication from "./pages/TeamCommunication";
import Help from "./pages/Help";
import Mailing from "./pages/Mailing";
import Overtime from "./pages/Overtime";
import axios from "axios";
import { logError } from "./services/errorLogger";

// Setup axios interceptor with retry for 429 errors
axios.interceptors.response.use(
  response => response,
  async error => {
    const config = error.config;
    
    // Retry logic for 429 (Too Many Requests)
    if (error.response?.status === 429 && !config._retry) {
      config._retry = true;
      config._retryCount = (config._retryCount || 0) + 1;
      
      // Max 3 retries with exponential backoff
      if (config._retryCount <= 3) {
        const delay = Math.min(1000 * Math.pow(2, config._retryCount - 1), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
        return axios(config);
      }
    }
    
    // Log non-429 errors or failed retries
    const errorMessage = error.response?.data?.detail || error.message || 'Erreur réseau';
    const url = error.config?.url || 'unknown';
    
    // Only log if not a 429 or if retries exhausted
    if (error.response?.status !== 429 || config._retryCount > 3) {
      logError(errorMessage, 'error', 'api', { url, status: error.response?.status });
    }
    
    return Promise.reject(error);
  }
);

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Extranet as default */}
      <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
      <Route path="/absences" element={<ProtectedRoute><Absences /></ProtectedRoute>} />
      
      <Route path="/stats" element={<ProtectedRoute allowedRoles={["manager", "directeur"]}><Stats /></ProtectedRoute>} />
      <Route path="/reporting" element={<ProtectedRoute><ReportingPlanning /></ProtectedRoute>} />
      <Route path="/leave" element={<ProtectedRoute><LeaveManagement /></ProtectedRoute>} />
      <Route path="/communicate" element={<ProtectedRoute allowedRoles={["manager", "directeur"]}><TeamCommunication /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute allowedRoles={["manager", "directeur"]}><Help /></ProtectedRoute>} />
      
      <Route path="/users" element={<ProtectedRoute allowedRoles={["directeur"]}><Users /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute allowedRoles={["directeur"]}><Admin /></ProtectedRoute>} />
      <Route path="/overtime" element={<ProtectedRoute allowedRoles={["directeur"]}><Overtime /></ProtectedRoute>} />
      <Route path="/mailing" element={<ProtectedRoute allowedRoles={["manager", "directeur"]}><Mailing /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
